import { reportError, setStepsProvider } from './report';

/**
 * Seeing the failures nobody reports.
 *
 * Every fault this week had to be inferred from side effects, because the
 * things that actually go wrong on these phones DO NOT THROW:
 *
 *   - iOS killing the tab for memory. The process is gone. No error event
 *     fires, no promise rejects, no handler runs. The app cannot report its own
 *     death.
 *   - "It froze." Nothing threw; the app was waiting on something slow.
 *   - "The scanner did not work." The camera was live and decoding and simply
 *     never found a barcode, which is not an error by any definition the code
 *     has.
 *
 * None of that reaches an error reporter, and only two drivers out of forty-nine
 * ever phone in. So this module records the ordinary progress of a session and
 * lets the NEXT launch tell us how the last one ended.
 *
 * WHAT IS RECORDED: which screen, which step, how long things took. Never a
 * patient, a recipient, an address or a barcode. This is about the software.
 */

interface Crumb { t: number; s: string }
interface Session {
  id: string;
  startedAt: number;
  lastSeen: number;
  clean: boolean;
  crumbs: Crumb[];
}

const KEY = 'jw.session';
const MAX_CRUMBS = 24;

/** Rough shape of the phone, to tell "every iPhone" from "one tired handset". */
function deviceNote(): string {
  const n = navigator as Navigator & {
    deviceMemory?: number; hardwareConcurrency?: number;
  };
  const bits = [
    n.deviceMemory ? `${n.deviceMemory}GB` : null,
    n.hardwareConcurrency ? `${n.hardwareConcurrency}cpu` : null,
    `${screen.width}x${screen.height}`,
  ].filter(Boolean);
  return bits.join(' ');
}

function read(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Session) : null;
  } catch { return null; }
}

let current: Session | null = null;
let writeTimer: ReturnType<typeof setTimeout> | null = null;

/*
 * Written through a short timer rather than on every crumb. A synchronous
 * localStorage write on each step of a flow is exactly the sort of small
 * constant cost that turns into "the app feels slow" -- and this module exists
 * to find that problem, not to add to it.
 */
function save(): void {
  if (writeTimer !== null) return;
  writeTimer = setTimeout(() => {
    writeTimer = null;
    try { if (current) localStorage.setItem(KEY, JSON.stringify(current)); }
    catch { /* full or private. Diagnostics never throw. */ }
  }, 400);
}

function saveNow(): void {
  if (writeTimer !== null) { clearTimeout(writeTimer); writeTimer = null; }
  try { if (current) localStorage.setItem(KEY, JSON.stringify(current)); }
  catch { /* as above */ }
}

/**
 * Note that something happened. Cheap, synchronous, never throws.
 *
 * Call it at the start of anything a driver waits for, and at anything that
 * changes what is on screen. The value is in the SEQUENCE: "stop, totes, scan,
 * scan, scan, scan" ending abruptly is a scanner that killed the tab, and no
 * single event in that list would have told us so.
 */
export function crumb(label: string): void {
  if (!current) return;
  const now = Date.now();
  current.lastSeen = now;
  current.crumbs.push({ t: now, s: label.slice(0, 32) });
  if (current.crumbs.length > MAX_CRUMBS) current.crumbs.shift();
  save();
}

/** Time something the driver is waiting on, and report it if it drags. */
export function timed<T>(
  label: string, slowMs: number, work: () => Promise<T>,
): Promise<T> {
  const began = Date.now();
  crumb(label);
  return work().then(
    (value) => { note(label, began, slowMs, null); return value; },
    (err: unknown) => {
      note(label, began, slowMs, (err as Error)?.message ?? 'failed');
      throw err;
    },
  );
}

function note(label: string, began: number, slowMs: number, err: string | null): void {
  const ms = Date.now() - began;
  crumb(`${label}:${ms}ms${err ? ' ERR' : ''}`);
  /*
   * Only the slow ones are sent. A stop that worked is already recorded as a
   * delivery; what we have never had is a number attached to "it took too
   * long". Now "the scanner is slow" is a row saying 11400ms on an iPhone.
   */
  if (ms >= slowMs) {
    reportError('slow', label, `${ms}ms${err ? ` after ${err}` : ''} ${deviceNote()}`);
  }
}

/**
 * Start the session, and report how the LAST one ended.
 *
 * This is the only way to see an iOS tab kill. The dead session cannot speak;
 * the next launch speaks for it. A session that was never marked clean, and
 * whose last crumb is close to when it stopped being seen, did not exit — it
 * was killed, and the crumbs say what the driver was doing at the time.
 */
/**
 * Mark the current session as ending on purpose.
 *
 * Exported so the service-worker update path can call it before reloading.
 * An app that updates itself is not an app that crashed, and reporting it as
 * one turns every deploy into a burst of false alarms -- which is exactly what
 * happened on 20 Sep: four 'died' reports, three of them a reload.
 */
export function endSessionCleanly(): void {
  if (!current) return;
  current.clean = true;
  saveNow();
}

/** Below this, an ending is a reload or a navigation, not a death. */
const TOO_SHORT_TO_BE_A_DEATH_MS = 5_000;

export function startSession(): void {
  const previous = read();
  // Lets a crash report say what the driver was doing just before it.
  setStepsProvider(() => (current ? current.crumbs.slice(-6).map((c) => c.s).join(' > ') : ''));

  current = {
    id: Math.random().toString(36).slice(2, 10),
    startedAt: Date.now(),
    lastSeen: Date.now(),
    clean: false,
    crumbs: [],
  };
  saveNow();

  /*
   * A session that lived a few seconds did not die -- it reloaded.
   *
   * On 20 Sep three of the four 'died' reports read `ran 0s` and `ran 3s`,
   * with a single `route:load` crumb and nothing else. That is the app
   * restarting, almost certainly picking up a new version, and reporting it as
   * a crash made a deploy look like a regression. Reading a number that has to
   * be explained away every time is worse than not having it.
   *
   * A real death has a trail: a driver got somewhere, did something, and then
   * the app stopped existing. That takes longer than five seconds.
   */
  const livedFor = previous ? previous.lastSeen - previous.startedAt : 0;

  if (previous && !previous.clean && previous.crumbs.length > 0
      && livedFor >= TOO_SHORT_TO_BE_A_DEATH_MS) {
    const ranFor = Math.round((previous.lastSeen - previous.startedAt) / 1000);
    const trail = previous.crumbs
      .map((c) => `${Math.round((c.t - previous.startedAt) / 1000)}s ${c.s}`)
      .join(' > ');
    /*
     * 'died' rather than 'crash': nothing threw. The app simply stopped
     * existing between one moment and the next, which is what a driver
     * experiences as the app closing itself or going white.
     */
    reportError('died', previous.crumbs[previous.crumbs.length - 1]?.s ?? 'unknown',
                `ran ${ranFor}s | ${deviceNote()} | ${trail}`);
  }

  /*
   * pagehide is the one iOS reliably fires when the driver leaves on purpose.
   * beforeunload is not dispatched on iOS at all, and unload is unreliable
   * everywhere, so both are registered and whichever arrives first wins.
   */
  const closing = () => {
    if (!current) return;
    current.clean = true;
    saveNow();
  };
  window.addEventListener('pagehide', closing);
  window.addEventListener('beforeunload', closing);

  /*
   * Coming BACK from the background reopens the session. Otherwise every
   * ordinary "checked the map, came back" would be filed as a clean exit and
   * the kill that followed would look like a fresh start.
   */
  document.addEventListener('visibilitychange', () => {
    if (!current) return;
    if (document.visibilityState === 'visible') {
      current.clean = false;
      crumb('resumed');
    } else {
      closing();
    }
  });
}
